/**
 * Study Attachment Hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { attachmentService } from './attachment.service'
import type { CreateAttachmentData } from '../../domain/attachment/types'

const QUERY_KEYS = {
  attachments: (studyId: string) => ['attachments', studyId],
  attachmentCount: (studyId: string) => ['attachment-count', studyId],
}

/**
 * Hook for listing attachments
 */
export function useStudyAttachments(studyId: string) {
  return useQuery({
    queryKey: QUERY_KEYS.attachments(studyId),
    queryFn: () => attachmentService.list(studyId),
    enabled: !!studyId,
  })
}

/**
 * Hook for getting attachment count
 */
export function useAttachmentCount(studyId: string) {
  return useQuery({
    queryKey: QUERY_KEYS.attachmentCount(studyId),
    queryFn: () => attachmentService.count(studyId),
    enabled: !!studyId,
  })
}

/**
 * Hook for uploading attachment
 */
export function useUploadAttachment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ studyId, data }: { studyId: string; data: CreateAttachmentData }) =>
      attachmentService.upload(studyId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.attachments(variables.studyId),
      })
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.attachmentCount(variables.studyId),
      })
    },
  })
}

/**
 * Hook for downloading attachment
 */
export function useDownloadAttachment() {
  return useMutation({
    mutationFn: ({ studyId, attachmentId }: { studyId: string; attachmentId: string }) =>
      attachmentService.download(studyId, attachmentId),
  })
}

/**
 * Hook for deleting attachment
 */
export function useDeleteAttachment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ studyId, attachmentId }: { studyId: string; attachmentId: string }) =>
      attachmentService.delete(studyId, attachmentId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.attachments(variables.studyId),
      })
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.attachmentCount(variables.studyId),
      })
    },
  })
}

export { attachmentService }
